<template>
	<div v-if="visible && value != null" class="fd-filter-item" :class="[{ 'fd-filter-item-big' : bigValue }, clazz]" :style="style">
		<template v-if="itemType === 'F'">
			<select class="fd-filter-join" v-model="value.join">
				<option value="AND">{{ env.msg('bas.and') }}</option>
				<option value="OR">{{ env.msg('bas.or') }}</option>
				<option value="AND_NOT">{{ env.msg('bas.andNot') }}</option>
				<option value="OR_NOT">{{ env.msg('bas.orNot') }}</option>
			</select>
			<select class="fd-filter-column" v-model="value.column">
				<option v-if="columnNotSelected"></option>
				<template v-if="filterableColumns"><option v-for="ol in filterableColumns" :key="ol.value" :value="ol.value">{{ ol.name }}</option></template>
			</select>
			<select v-if="filteredColumn" class="fd-filter-operator" v-model.number="value.op">
				<option v-if="operatorNotSelected"></option>
				<option v-for="ol in allowedOperators" :key="ol.value" :value="ol.value">{{ ol.name }}</option>
			</select>
			<filter-value-editor v-if="hasEditor" :column="filteredColumn" :fi="value" :big-value="bigValue" @bigvalue="switchToBigValue"></filter-value-editor>
		</template>

		<template v-else-if="itemType === 'G'">
			<select class="fd-filter-join" v-model="value.join">
				<option value="AND">{{ env.msg('bas.and') }}</option>
				<option value="OR">{{ env.msg('bas.or') }}</option>
				<option value="AND_NOT">{{ env.msg('bas.andNot') }}</option>
				<option value="OR_NOT">{{ env.msg('bas.orNot') }}</option>
			</select>
			<div class="filter-nested">
				<div class="filter-group-open"></div>
				<array-part :data="value" :part="groupArrayPart" :readonly="ro" :viewmode="vm"></array-part>
				<div class="filter-group-close"></div>
			</div>
		</template>

		<template v-else-if="itemType === 'N'">
			<select class="fd-filter-join" v-model="value.join">
				<option value="AND">{{ env.msg('bas.and') }}</option>
				<option value="OR">{{ env.msg('bas.or') }}</option>
				<option value="AND_NOT">{{ env.msg('bas.andNot') }}</option>
				<option value="OR_NOT">{{ env.msg('bas.orNot') }}</option>
			</select>
			<div class="filter-nested">
				<div class="filter-group-open"></div>
				<div class="filter-nested-content">
					<div class="filter-row-col-op">
						<select v-model.number="value.op" style="margin-right:4px">
							<option value="14">{{ env.msg('list.filter.ref.14') }}</option>
							<option value="15">{{ env.msg('list.filter.ref.15') }}</option>
						</select>
						<select v-model="refAgendaKey">
							<option v-for="ref in refList" :value="ref.value" :key="ref.value">{{ ref.name }}</option>
						</select>
					</div>
					<div>
						<div class="multi-select-inline fd-small" style="margin-top:4px">
							<label v-for="col in refColumns"><input type="checkbox" :checked="columnChecked(col.value)" @change="setColumnCheck($event, col.value)"> {{ col.name }}</label>
						</div>
						<filter-part v-if="nestedFilterPart" :child="true" :data="view" :part="nestedFilterPart" :readonly="ro" :viewmode="vm" ref="nestedFilter"></filter-part>
					</div>
				</div>
				<div class="filter-group-close"></div>
			</div>
		</template>

		<sym-icon name="minus-circle-16-full" class="fd-filter-item-closer" @click="deleteMe"></sym-icon>
	</div>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor";
import {injectEnv} from "@fd/Environment";
import {computed, inject, onMounted, provide, type Ref, ref, watch, watchEffect, type WritableComputedRef} from "vue";
import {
	defaultOperator,
	Filter,
	type FilterEntry,
	type FilterGroup,
	FilterItem,
	type FilterNested,
	operatorList,
	type OperatorListItem
} from "@t/agenda/Filter";
import {buildAgendaKey, parseAgendaKey, useAgendaStore} from "@/store/AgendaStore";
import {descriptorKey, MAX_ARRAY_VALUES, registrationKey} from "@fd/editors/filter/FilterUtils";
import type {ColumnListItem} from "@t/agenda/Columns";
import {ColumnType} from "@t/agenda/Columns";
import FilterValueEditor from "@fd/editors/filter/FilterValueEditor.vue";
import {FilterItemPartType} from "@t/fd/editor/Filter.ts";
import ArrayPart from "@fd/containers/ArrayPart.vue";
import type {Array, ArrayAddButton} from "@t/fd/layout/Array.ts";
import SymIcon from "@/fd/helpers/SymIcon.vue";
import {Agenda, AgendaClass, AgendaDescriptor, AgendaReference, AgendaRegistryEntry, referenceToDescriptor} from "@t/agenda/Agenda.ts";
import {ListItem, ListItemBase} from "@t/list/List.ts";
import {type View, ViewColumns, ViewProperties} from "@t/agenda/View.ts";
import FilterPart from "@fd/editors/filter/FilterPart.vue";
import type {FilterPart as FilterPartType} from "@t/fd/editor/Filter.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const fip = computed<FilterItemPartType>(() => props.part as FilterItemPartType);

const { visible, value, clazz, style, ro, vm } = useEditor(props);
const env = injectEnv();
const ast = useAgendaStore();
const emit = defineEmits(["array-delete"]);
const nestedFilter = ref(null);

const columnNotSelected = computed<boolean>(() => filteredColumn.value == null);
const operatorNotSelected = computed<boolean>(() => !value.value.op);

const values = computed<string[]>({
	get():string[] {
		return filterValues.value;
	},
	set(v:string[]):void {
		value.value.values = v;
	}
});

// Type of current item
const itemType = computed<"F" | "G" | "N">(() => (value.value as FilterItem)?.type);


const allOperators = computed<OperatorListItem[]>(() => {
	return operatorList(env.msg);
});

const filteredColumn = computed<ColumnListItem|null>(() => {
	if (value.value?.column && Array.isArray(filterableColumns.value)) {
		for (let item of filterableColumns.value) {
			if (item.value === value.value.column) return item;
		}
	}
	return null;
});

function normalizeOperator(operator:number|string, columnType: string):number {
	for (let op of allOperators.value) {
		if (op.value === operator.toString()) {
			// Found current operator
			if (op.allowed.indexOf(columnType) >= 0) return typeof operator === "number" ? operator : parseInt(operator);
			break;
		}
	}
	// Current operator can't be used for given column
	for (let op of allOperators.value) {
		if (op.allowed.indexOf(columnType) >= 0) return parseInt(op.value);
	}
	return 0;	// suitable operator not found
}

const allowedOperators = computed<OperatorListItem[]>(() => {
	const filtered:OperatorListItem[] = [];
	const fc = filteredColumn.value;
	if (fc != null) {
		for (let op of allOperators.value) {
			if (op.allowed.indexOf(fc.type) >= 0) filtered.push(op);
		}
	}
	return filtered;
});

const agendaDescriptor = inject(descriptorKey);
const filterableColumns = ref<ColumnListItem[]>([]);

watch(agendaDescriptor, (desc) => {
	ast.getFilterColumns(desc).then((cols) => {
		filterableColumns.value.length = 0;
		filterableColumns.value.push(...cols);
	});
}, { deep: true, immediate: true });


function extractViewProps(agendaObject:AgendaReference) {
	if (agendaObject) {
		if (agendaObject.view) {
			if (agendaObject.view.props != null) return agendaObject.view.props;
		}
	}
	return null;
}

interface AgendaRefListItem extends ListItemBase, AgendaClass, ViewColumns {
	view?: ViewProperties
}

const filterValues = computed<string[]>(() => {
	if (!Array.isArray(value.value.values)) value.value.values = [];
	return value.value.values;
});

const conditions = computed<string[]>(() => {
	if (!Array.isArray(value.value.conditions)) value.value.conditions = [];
	return value.value.conditions;
});

const agenda = computed<Agenda>(() => {
	if (typeof value.value.agenda !== "object") value.value.agenda = {};
	return value.value.agenda;
});

const view = computed<View>(() => {
	const a = agenda.value;
	if (typeof a.view !== "object") a.view = {};
	return a.view;
});

const viewFilter = computed<Filter>(() => {
	const v = view.value;
	if (typeof v.filter !== "object") v.filter = { conditions: [] };
	return v.filter;
});

const viewColumns = computed<string[]>(() => {
	const v = view.value;
	if (!Array.isArray(v.columns)) v.columns = [];
	return v.columns;
});


function columnChecked(colname:string):boolean {
	return viewColumns.value.indexOf(colname) >= 0;
}

function setColumnCheck(event:Event, colname:string) {
	const state = (event.target as HTMLInputElement).checked;
	if (state) {
		// Add if missing
		const itemIdx = viewColumns.value.indexOf(colname);
		if (itemIdx < 0) viewColumns.value.push(colname);
	} else {
		// Remove if present
		const itemIdx = viewColumns.value.indexOf(colname);
		if (itemIdx >= 0) viewColumns.value.splice(itemIdx, 1);
	}

}

const nestedFilterPart = computed<FilterPartType|null>(() => {
	if (selectedRefItem.value) {
		const filterPart:FilterPartType = {
			type: "filter",
			property: "filter",
			descriptor: {
				agenda: selectedRefItem.value.agenda
			}
		};
		if (selectedRefItem.value.entity) filterPart.descriptor.entity = selectedRefItem.value.entity;
		if (selectedRefItem.value?.view?.props) filterPart.descriptor.props = selectedRefItem.value.view.props;
		return filterPart;
	} else {
		return null;
	}
});


const hasEditor = computed<boolean>(() => {
	const fc = filteredColumn.value;
	const op = value.value.op;
	return fc != null && fc.type !== ColumnType.BOOLEAN && op && !(op == 10 || op == 11 /* NULL || NOT NULL */);
});

const multivalue = computed<boolean>(() => {
	return value.value.op === 14 || value.value.op === 15; /* IN || NOT IN*/
});

const bigValueForced = ref<boolean>(false);
const bigValue = computed<boolean>(() => {
	if (itemType.value !== 'F') return false;	// big value only for "F"
	return multivalue.value && (bigValueForced.value || values.value.length > MAX_ARRAY_VALUES);
});
function switchToBigValue() {
	bigValueForced.value = true;
}

const groupArrayPart = computed<Array>(() => {
	const arrayPart:Array = {
		type: "array",
		sortable: true,
		property: "conditions",
		objectMode: true,
		add: [
			<ArrayAddButton>{
				label: env.msg("butt.addCondition.F"),
				init: "value",
				value: <FilterEntry>{
					type: "F",
					join: "AND",
					values: []
				}
			},
			<ArrayAddButton>{
				label: env.msg("butt.addCondition.G"),
				init: "value",
				value: <FilterGroup>{
					type: "G",
					join: "AND",
					conditions: []
				}
			}
		],
		item: <FilterItemPartType>{
			type: "filter-item"
		}
	};

	// if (descriptor.value != null && descriptor.value.type && Array.isArray(descriptor.value.references) && descriptor.value.references.length > 0) {
	// 	arrayPart.add?.push(<ArrayAddButton>{
	// 		label: env.msg("butt.addCondition.N"),
	// 		init: "value",
	// 		value: <FilterNested>{ join: "AND", type: "N" }
	// 	});
	// }

	return arrayPart;
});

function deleteMe() {
	emit("array-delete");
}

function normalizeOperatorForColumn(col:ColumnListItem|null) {
	if (col != null) {
		const currentOp = value.value.op;
		if (currentOp) {
			// Test if current operator can be used for given column
			for (let ao of allowedOperators.value) {
				if (parseInt(ao.value) === currentOp) {
					return;
				}
			}
		}
		// Set default operator
		let op = defaultOperator.get(col.type);
		if (!op && allowedOperators.value.length > 0) {
			op = parseInt(allowedOperators.value[0].value);	// first available
		}
		value.value.op = op;	// set default
	}
}

// User changed column
watch (() => filteredColumn.value, (col) => {
	normalizeOperatorForColumn(col);
});

/****************** NESTED FILTER SUPPORT ******************/

const registration:Ref<AgendaRegistryEntry|null> = inject(registrationKey);

const refAgendaKey = computed<string>({
	get():string {
		const ag = value.value?.agenda?.agenda;
		const en =  value.value?.agenda?.entity;
		let vp = null;
		if (value.value?.ageda?.view) vp = value.value?.ageda.view.props;
		if (ag) return buildAgendaKey(ag, en, vp);
		return "";
	},
	set(v:string):void {
		let parsed:AgendaDescriptor = parseAgendaKey(v);
		//console.log("agenda key "+v+" parsed to "+JSON.stringify(parsed));
		const a:Agenda = agenda.value;
		if (parsed && parsed.agenda) {
			a.agenda = parsed.agenda;
			if (parsed.entity) a.entity = parsed.agenda;
			else delete a.entity;
			if (parsed.props) view.value.props = parsed.props;
			else delete view.value.props;
		} else {
			delete a.agenda;
			delete a.entity;
			delete view.value.props;
		}
	}
});


const refList = ref<AgendaRefListItem[]>([]);

watchEffect(() => {
	if (itemType.value === 'N') {
		if (registration.value && Array.isArray(registration.value.references)) {
			refList.value.length = 0;
			for (let ref of registration.value.references) {
				ast.lookupRegistry(referenceToDescriptor(ref)).then((entry:AgendaRegistryEntry) => {
					const o:Partial<AgendaRefListItem> = {};
					o.value = buildAgendaKey(ref.agenda, ref.entity, ref.view ? ref.view.props : null);
					const vp = extractViewProps(ref);
					o.name = entry.name;
					// ref.view.columns contains names of referencing columns
					o.agenda = ref.agenda;
					if (ref.entity) o.entity = ref.entity;
					o.columns = ref.view.columns;
					if (vp) o.view = { props: vp };
					refList.value.push(o as AgendaRefListItem);
					// If no agenda selected and we have any references
					// set editor to the first reference
					if (refAgendaKey.value.length === 0) {
						refAgendaKey.value = o.value;
					}
				});
			}
		}
	}
});

function findAgendaRef(key:string):AgendaRefListItem | null {
	for (let refItem of refList.value) {
		if (refItem.value === key) {
			return refItem;
		}
	}
	return null;
}

/** Selected nested agenda info */
const selectedRefItem = computed<AgendaRefListItem|null>(() => {
	return findAgendaRef(refAgendaKey.value);
});

const refColumns = ref<ListItemBase[]>([]);

watchEffect(() => {
	if (itemType.value === 'N') {
		let selected = selectedRefItem.value;
		if (selected != null && Array.isArray(selected.columns)) {
			const desc:AgendaDescriptor = {
				agenda: selected.agenda,
				entity: selected.entity
			};
			if (selected.view) desc.props = selected.view.props;
			ast.getFilterColumns(desc).then((otherAgendaColumns:ColumnListItem[]) => {
				refColumns.value.length = 0;
				for (let c of otherAgendaColumns) {
					if (selected.columns.indexOf(c.value) >= 0) {
						refColumns.value.push(c);
					}
				}
			});
		}
	}
});

if (itemType.value === 'N') {

	watch(refAgendaKey, (key) => {
		// Push all referencing columns (small checkboxes under agenda select)
		const cols = viewColumns.value;
		cols.length = 0;
		if (key) {
			// add all columns which can reference the table to the array
			const ri: AgendaRefListItem = findAgendaRef(key);
			if (ri) {
				for (let col of ri.columns) cols.push(col);
			}
		}
		// Delete all previous conditions
		if (nestedFilter.value) nestedFilter.value.discardConditions();
	});

	// This watcher will set proper column as primary key of current agenda
	watch(filterableColumns, (cols:ColumnListItem[]) => {
		let pkColumn:string = null;
		if (cols.length > 0) {
			for (let fc of cols) {
				if (fc.primaryKey) {
					pkColumn = fc.value;
					break;
				}
			}
		}
		if (pkColumn != null) value.value.column = pkColumn;
		else delete value.value.column;
	}, { deep: true });
}

</script>

<style>
.fd-filter-item {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	column-gap: 4px;
	padding: 4px 12px 4px 4px;
	border-radius: var(--button-radius);
	border: 1px solid var(--border-color);
	position: relative;
	margin-right: 8px;
	transition: border-color 500ms;

}
.fd-array-item[data-index="0"] > .fd-array-item-content > .fd-filter-item > .fd-filter-join {
	opacity: 0.1;
}


.fd-filter-item.fd-filter-item-big,
.fd-filter-item:has(> .require-big-value) {
	box-sizing: border-box;
	display: grid;
	grid-template-columns: auto auto auto;
	grid-template-rows: auto auto;
	row-gap: 4px;
	justify-content: stretch;

	& > .fd-filter-join {
		grid-column: 1 / 2;
		grid-row: 1 / 2;
	}
	& > .fd-filter-column {
		grid-column: 2 / 3;
		grid-row: 1 / 2;
	}
	& > .fd-filter-operator {
		grid-column: 3 / 4;
		grid-row: 1 / 2;
	}
	& > .fd-filter-editor {
		grid-column: 1 / 4;
		grid-row: 2 / 3;
	}
}

.fd-filter-item-closer {
	position: absolute;
	top: calc(50% - 9px);
	right: -8px;
	color: var(--form-fg-min);
	background-color: var(--bg);
	cursor: pointer;
	transition: color 500ms;
}

.fd-filter-item:hover .fd-filter-item-closer {
	color: var(--form-fg);
}
.fd-filter-item-closer:hover {
	color: var(--red) !important;
}
</style>
